import { useState, useCallback, useEffect, useRef } from "react";
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";

/**
 * Computes a shallow list of keys that differ between two objects of the same
 * type. Works for plain record-like objects (one level deep).
 */
function shallowDirtyFields<T extends Record<string, unknown>>(
  initial: T,
  current: T,
): string[] {
  const keys = new Set([...Object.keys(initial), ...Object.keys(current)]);
  const dirty: string[] = [];
  keys.forEach((k) => {
    if (JSON.stringify(initial[k]) !== JSON.stringify(current[k])) {
      dirty.push(k);
    }
  });
  return dirty;
}

export interface UseDirtyStateResult<T> {
  isDirty: boolean;
  /** List of top-level keys that differ from initial value. */
  dirtyFields: string[];
  currentValue: T;
  setCurrentValue: (v: T) => void;
  reset: (newInitial?: T) => void;
  /**
   * Call this instead of closing directly when the user tries to leave.
   * If isDirty, opens a confirmation Dialog. onConfirm is called if they
   * choose to discard changes. If not dirty, onConfirm is called immediately.
   */
  requestExit: (onConfirm: () => void) => void;
  /** Render this alongside your component — it renders the confirm Dialog. */
  ExitGuardDialog: React.FC;
}

export function useDirtyState<T>(initial: T): UseDirtyStateResult<T> {
  const [initialValue, setInitialValue] = useState<T>(initial);
  const [currentValue, setCurrentValue] = useState<T>(initial);
  const [dialogOpen, setDialogOpen] = useState(false);
  const onConfirmRef = useRef<(() => void) | null>(null);

  const isDirty = JSON.stringify(currentValue) !== JSON.stringify(initialValue);

  const dirtyFields: string[] =
    isDirty &&
    typeof initialValue === "object" &&
    initialValue !== null &&
    typeof currentValue === "object" &&
    currentValue !== null
      ? shallowDirtyFields(
          initialValue as Record<string, unknown>,
          currentValue as Record<string, unknown>,
        )
      : [];

  const reset = useCallback(
    (newInitial?: T) => {
      const v = newInitial !== undefined ? newInitial : initialValue;
      setInitialValue(v);
      setCurrentValue(v);
    },
    [initialValue],
  );

  const requestExit = useCallback(
    (onConfirm: () => void) => {
      if (!isDirty) {
        onConfirm();
        return;
      }
      onConfirmRef.current = onConfirm;
      setDialogOpen(true);
    },
    [isDirty],
  );

  // Guard browser refresh / tab close when dirty.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleDiscard = () => {
    setDialogOpen(false);
    onConfirmRef.current?.();
    onConfirmRef.current = null;
  };

  const handleKeep = () => {
    setDialogOpen(false);
    onConfirmRef.current = null;
  };

  const ExitGuardDialog: React.FC = () => (
    <Dialog open={dialogOpen} onClose={handleKeep} maxWidth="xs" fullWidth>
      <DialogTitle>Alterações por guardar</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Tens alterações por guardar. Se saíres agora vão ser perdidas.
        </Typography>
        {dirtyFields.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Campos alterados: {dirtyFields.join(", ")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleKeep} variant="outlined" color="inherit">
          Cancelar
        </Button>
        <Button onClick={handleDiscard} variant="contained" color="error">
          Sair sem guardar
        </Button>
      </DialogActions>
    </Dialog>
  );

  return {
    isDirty,
    dirtyFields,
    currentValue,
    setCurrentValue,
    reset,
    requestExit,
    ExitGuardDialog,
  };
}
